// Vendored from echoes-sh/guard packages/guard (@echoes/guard 0.1.0). Replace with the npm package once published.
/** Hosted Echoes Guard endpoints (used when neither options nor env provide one). */
export const GUARD_DEFAULT_API_URL = "https://api.guard.echoes.sh";
export const GUARD_DEFAULT_KV_URL = "https://kv.guard.echoes.sh";

/** Reads an env var without assuming a Node `process` (browsers, React Native, Convex). */
export function readEnv(name: string): string | undefined {
  const env = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env;
  const value = env?.[name]?.trim();
  return value ? value : undefined;
}

function trimSlash(url: string): string {
  return url.replace(/\/+$/, "");
}

/**
 * The plugins send the project API key to these URLs on every call, so they must be https.
 * Plain http is accepted for `localhost` and `127.0.0.1` only (local Guard, tests).
 */
export function assertSecureGuardUrl(url: string, name: string): void {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error(`@echoes/guard: ${name} is not a valid URL: "${url}"`);
  }
  if (parsed.protocol === "https:") return;
  if (parsed.protocol === "http:" && (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1")) return;
  throw new Error(
    `@echoes/guard: ${name} must use https (got "${url}"). The API key would travel in clear text. ` +
      "Plain http is only accepted for http://localhost and http://127.0.0.1.",
  );
}

/** API base URL: explicit value, then `GUARD_API_URL`, then the hosted default. */
export function resolveGuardApiUrl(apiUrl?: string): string {
  return trimSlash(apiUrl?.trim() || readEnv("GUARD_API_URL") || GUARD_DEFAULT_API_URL);
}

/** KV base URL: explicit value, then `GUARD_KV_URL`, then the hosted default. */
export function resolveGuardKvUrl(kvUrl?: string): string {
  return trimSlash(kvUrl?.trim() || readEnv("GUARD_KV_URL") || GUARD_DEFAULT_KV_URL);
}

/**
 * Project-scoped ingestion URL for `sentinelClient({ identifyUrl })`.
 * The client POSTs to `${identifyUrl}/identify`, i.e. `{kv}/projects/{publicId}/identify`.
 *
 * In browser bundles env vars are usually not available at runtime, so pass
 * `kvUrl` explicitly (e.g. from `import.meta.env`) when not using the hosted KV.
 */
export function guardIdentifyUrl(publicId: string, kvUrl?: string): string {
  if (!publicId || !publicId.trim()) throw new Error("guardIdentifyUrl: publicId is required");
  return `${resolveGuardKvUrl(kvUrl)}/projects/${encodeURIComponent(publicId.trim())}`;
}
